import React, { useEffect, useState, useContext, useMemo } from "react";
import VisualBuilder from "./pages/VisualBuilder";
import GlobalContext, { globalVariables } from "./GlobalContext";
import { useMyContext } from "./MyContext";
import { useDataCache } from "./DataCacheContext";
import { isMobile } from "react-device-detect";
import { fetchSecure, sendChatMessage } from "./utils/utils";
import MenuEditor from "./pages/MenuEditor";
import UnifiedEditor from "./pages/UnifiedEditor";
import PageEditor from "./components/PageEditor";
import EditMode from "./pages/EditMode";

// Vik (Jan 14, 2026) : ThemeProvider must wrap the application to ensure dynamic theme variables
// are injected into the root element and accessible by all child components.
import { ThemeProvider } from "./ThemeContext";
import "./index.css";
import "./responsive.css";

import {
  BrowserRouter as Router,
  Switch,
  Route,
  useHistory,
  Redirect,
} from "react-router-dom";
import { auth } from "./firebase/config";
import { useAuthState } from "react-firebase-hooks/auth";

// Components
import Header from "./components/Header";
// import HeaderWix from "./components/HeaderWix";
import UserRoute from "./components/UserRoute";
// import ChatBot from "./components/ChatBot";
// import WixLoginCallback from "./components/WixLoginCallback";
// import NewPlanTable from "./components/NewPlanTable";
// import WixGetPlans from "./components/WixGetPlans";
import Loading from "./components/Loading";
// import Stripe from "./components/Stripe";
import LayoutRenderer from "./components/LayoutRenderer";

// Pages
import Home from "./pages/Home";
import Playground from "./pages/Playground";
// import Integrations from "./pages/Integrations";
// import Marketing from "./pages/Marketing";
// import Console from "./pages/Console";
// import Agents from "./pages/Agents";
// import UserAdmin from "./pages/UserAdmin";
// import Tester from "./pages/Tester";
// import Help from "./pages/Help";
// import Contact from "./pages/Contact";
// import SystemAdmin from "./pages/SystemAdmin";
// import QuickStart from "./pages/QuickStartNew";

import {
  useSuperAppConfig,
  useSuperAppConfigWithState,
  getSuperAppConfigSync,
} from "./lib/superappConfig";
import NewMenu from "./pages/NewMenu";
import LowCodePage from "./components/LowCodePage";
import AppSelector from "./pages/AppSelector";
import HeaderManager from "./pages/HeaderManager";

// Auth / legal / misc
import Signin from "./pages/Signin";
import Signup from "./pages/Signup";
// import Subscription from "./pages/Subscription";
import Privacy from "./pages/Privacy";
import TOS from "./pages/TOS";
// import Capabilities from "./pages/Capabilities";
// import Whitepapers from "./pages/Whitepapers";

/* Private/Public routes */
const PrivateRoute = ({
  component: Component,
  isLoggedIn,
  isLoading,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isLoading ? (
          <Loading />
        ) : isLoggedIn ? (
          <Component {...props} {...rest} />
        ) : (
          <Redirect to="/signin" />
        )
      }
    />
  );
};

const PublicRoute = ({
  component: Component,
  isLoggedIn,
  restricted,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isLoggedIn && restricted ? (
          <Redirect to="/home" />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
};

/**
 * MAIN APPLICATION COMPONENT
 *
 * Central hub that orchestrates routing, authentication, configuration loading,
 * and rendering of the SuperApp platform. Connects all major features together.
 *
 * KEY RESPONSIBILITIES:
 *
 * 1. SUPERAPP CONFIGURATION MANAGEMENT
 *    - Loads config from Firestore using useSuperAppConfigWithState() hook
 *    - Falls back to sync cache if hook hasn't loaded yet
 *    - Handles both wrapped { config: {...} } and unwrapped {...} formats
 *    - Shows loading screen while config is being fetched
 *    - Config structure: { header, features, layout, screens, app_config }
 *
 * 2. HEADER RENDERING
 *    - Extracts menu items from header.primaryMenu.items (with fallbacks to menu/navigation)
 *    - Passes config to <Header> component which renders navigation and brand
 *    - Handles Wix iframe login scenarios (renders HeaderWix instead)
 *    - Listens to 'superapp-config-updated' event for real-time header updates
 *    - Menu items structure: [{ key, label, path, visible, order, children: [...] }]
 *
 * 3. DYNAMIC ROUTE CREATION
 *    - Creates routes from menu items (parent and child routes)
 *    - Creates routes from layout config (layout.route)
 *    - Creates routes from screens config (screens object)
 *    - Route priority: Child routes → Parent routes → Layout routes → Screen routes → Catch-all
 *    - All dynamic routes render LowCodePage component which loads page config
 *
 * 4. SUPERAPP OBJECT CREATION & RENDERING
 *    - Objects created via PageEditor or MenuEditor
 *    - Stored in: superapp_objects collection (via /api/superapp/objects)
 *    - When used in pages: Config copied to app_config[objectKey] in SuperApp config
 *    - Rendering: LowCodeBlock loads from app_config and renders SuperAppObject component
 *    - Flow: Create object → Use in page → Copy to app_config → Render via LowCodeBlock
 *
 * 5. AUTHENTICATION & USER MANAGEMENT
 *    - Simplified login check: Only requires user ID string to exist
 *    - Loads user from session or URL parameters
 *    - Handles Wix iframe login scenarios
 *    - Manages user deep data (roles, plan, permissions)
 *
 * 6. ROUTE PROTECTION
 *    - PrivateRoute: Requires login, redirects to /signin if not logged in
 *    - PublicRoute: Can restrict logged-in users (e.g., signin/signup pages)
 *    - Editor routes: Page Editor (dev OR logged in + system admin)
 *                    Menu Editor (logged in)
 *                    Edit Mode (logged in + system admin OR dev)
 *
 * COMPONENT HIERARCHY:
 * App
 * ├── Router
 * │   ├── Header (conditional, based on iframeLogin)
 * │   │   └── Menu Items (from config.header.primaryMenu.items)
 * │   └── Switch
 * │       ├── Page Editor Route (/page-editor/:appId?)
 * │       ├── Menu Editor Route (/menu-editor)
 * │       ├── Dynamic Routes (from menu items → LowCodePage)
 * │       ├── Layout Routes (from layout config → LayoutRenderer)
 * │       ├── Screen Routes (from screens config → LowCodePage)
 * │       └── Catch-all Route (* → LowCodePage with "Build This Page" button)
 * └── ChatBot (conditional, based on data.showAdminToolChat)
 *
 * EVENT SYSTEM:
 * - Dispatches 'superapp-config-updated' when config changes (from editors)
 * - Header listens to this event to update menu/display in real-time
 * - Allows immediate UI updates without page refresh
 *
 * @see {@link ./docs/APP_ARCHITECTURE.md} - Complete App.js architecture documentation
 * @see {@link ./docs/MENU_EDITOR.md} - How headers are configured
 * @see {@link ./docs/PAGE_EDITOR.md} - How pages are created
 * @see {@link ./docs/SUPERAPP_OBJECTS.md} - How SuperAppObjects are created and rendered
 *
 * External Resources:
 * - React Router: https://reactrouter.com/
 * - React Hooks: https://react.dev/reference/react
 * - Firestore: https://firebase.google.com/docs/firestore
 * - React Context: https://react.dev/learn/passing-data-deeply-with-context
 */

// ... existing imports ...

function MainApp() {
  /* ---------- SuperApp config ---------- */
  const appId = new URLSearchParams(window.location.search).get("appId");

  // If no appId is present, render the App Selector (Lobby)
  // But only if we are at the root path or explicitly asking for it
  // We need to allow other routes like /signin, /signup etc to work without appId if needed
  // For now, let's say if we are at root "/" and no appId, show selector.
  const isRoot =
    window.location.pathname === "/" || window.location.pathname === "";

  // Hook needs to be called unconditionally, so we pass a dummy ID if none exists
  // But we'll conditionally render based on appId presence later
  const {
    config: cfgRaw,
    loading: configLoading,
    error: configError,
  } = useSuperAppConfigWithState(appId || "dummy-no-load");

  // Fallback to sync cache if hook hasn't loaded yet
  const cfgRawFallback =
    cfgRaw || getSuperAppConfigSync(appId || "dummy-no-load");
  // Unwrap if the server returns { config: {...} }
  const cfg =
    cfgRawFallback && cfgRawFallback.config
      ? cfgRawFallback.config
      : cfgRawFallback;

  const layoutCfg = cfg?.layout;

  const layoutRoutes = useMemo(() => {
    if (!layoutCfg) return [];
    const normalize = (route) => {
      if (!route) return "/";
      const str = String(route);
      if (str === "*") return "*";
      return str.startsWith("/") ? str : `/${str}`;
    };
    if (Array.isArray(layoutCfg.routes) && layoutCfg.routes.length > 0) {
      return layoutCfg.routes.map(normalize);
    }
    if (typeof layoutCfg.route === "string" && layoutCfg.route.length > 0) {
      return [normalize(layoutCfg.route)];
    }
    if (layoutCfg.route === false) return [];
    return ["/"];
  }, [layoutCfg]);
  const layoutRouteSet = useMemo(() => new Set(layoutRoutes), [layoutRoutes]);

  /**
   * HEADER MENU ITEMS EXTRACTION
   *
   * Unified accessor for menu items with fallback chain for backward compatibility:
   * 1. header.primaryMenu.items (preferred, new format)
   * 2. menu (legacy format)
   * 3. navigation (legacy format)
   * 4. [] (empty array if none found)
   *
   * These menu items are:
   * - Passed to <Header> component for rendering navigation
   * - Used to create dynamic routes (see route creation below)
   * - Updated in real-time when Menu Editor saves changes
   *
   * Menu item structure:
   * {
   *   key: "docs",
   *   label: "Documentation",
   *   path: "/docs",
   *   visible: true,
   *   order: 2,
   *   children: [{ key: "guides", label: "Guides", path: "/docs/guides" }]
   * }
   */
  const cfgMenuItems =
    (Array.isArray(cfg?.header?.primaryMenu?.items) &&
      cfg.header.primaryMenu.items) ||
    (Array.isArray(cfg?.menu) && cfg.menu) ||
    (Array.isArray(cfg?.navigation) && cfg.navigation) ||
    [];

  // Low-code page is used for dynamic routes
  /* ------------------------------------ */

  const [id, setId] = useState("");
  const [user, setUser] = useState("");
  const [userDeep, setUserDeep] = useState({});
  const [iframeLogin, setIframeLogin] = useState(null);
  const [showLoginErrorMessage, setShowLoginErrorMessage] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const globalData = useContext(GlobalContext);
  /* Vik (Jan 21, 2026) : Added setUserId to sync user ID to context */
  const {
    data,
    setUserData,
    userId: contextUserId,
    setUserId,
    authConfig,
    setAuthConfig,
  } = useMyContext();
  const { initDataCache } = useDataCache();
  const [planIds, setPlanIds] = useState([]);
  const [isFinishRegistration, setFinishRegistration] = useState(false);
  const [showChatBotPopup, setShowChatBotPopup] = useState(false);
  const [chatbotSrc, setChatbotSrc] = useState(null);

  let pathName = window.location.pathname.substring(1);

  /**
   * Check if user is logged in
   *
   * Simplified login check that only requires the user ID string to be present.
   * Previously, this also required userDeep to have keys, which caused issues
   * when userDeep hadn't loaded yet (resulting in false "Access Denied" errors).
   *
   * @returns {boolean} true if user ID exists, false otherwise
   */
  const isLoggedIn = () => {
    // Only check if user ID exists - don't require userDeep to be loaded
    // This prevents false negatives when userDeep is still loading from the API
    return !!(user && typeof user === "string" && user.length > 0);
  };

  useEffect(() => {
    // Defensive check - ensure userDeep is a valid non-null object before using Object.keys
    // Explicit null check (typeof null === 'object' in JavaScript!)
    if (userDeep === null || userDeep === undefined) return;
    if (typeof userDeep !== "object") return;
    if (Array.isArray(userDeep)) return;

    try {
      // Double-check right before calling Object.keys
      if (
        userDeep === null ||
        userDeep === undefined ||
        typeof userDeep !== "object" ||
        Array.isArray(userDeep)
      )
        return;
      const keys = Object.keys(userDeep);
      const src =
        keys.length > 0 ? data?.adminToolChatBot : data?.websiteChatBotURL;
      if (src && src !== chatbotSrc) setChatbotSrc(src);
    } catch (e) {
      console.warn("Error setting chatbot src:", e);
      // Don't crash - just skip setting the chatbot src
    }
  }, [data, userDeep, chatbotSrc]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Vik (Jan 21, 2026) : Sync local user state to context so child components (like LowCodeBlock) can access it without prop drilling */
  useEffect(() => {
    if (user && setUserId) {
      setUserId(user);
    }
  }, [user, setUserId]);

  useEffect(() => {
    if (!chatbotSrc) return;
    try {
      const origin = new URL(chatbotSrc).origin;

      const linkPreconnect = document.createElement("link");
      linkPreconnect.rel = "preconnect";
      linkPreconnect.href = origin;
      linkPreconnect.crossOrigin = "";

      const linkDns = document.createElement("link");
      linkDns.rel = "dns-prefetch";
      linkDns.href = origin;

      document.head.appendChild(linkPreconnect);
      document.head.appendChild(linkDns);

      return () => {
        document.head.removeChild(linkPreconnect);
        document.head.removeChild(linkDns);
      };
    } catch {}
  }, [chatbotSrc]);

  function updateDimensions() {
    const iframe = document.getElementById("myIframe");
    const chatCloseBtn = document.getElementById("chatCloseBtn");
    if (iframe && chatCloseBtn) {
      const iframeRect = iframe.getBoundingClientRect();
      const viewport = document.documentElement.getBoundingClientRect();
      chatCloseBtn.style.position = "fixed";
      chatCloseBtn.style.right =
        viewport.width - (iframeRect.right - 10) + "px";
      chatCloseBtn.style.top = iframeRect.y + 10 + "px";
    }
  }

  window.addEventListener("resize", updateDimensions);

  const [users /* setUsers */] = useState({ data: [], status: "IDLE" });
  const [reference /* setReference */] = useState({ data: [], status: "IDLE" });
  const history = useHistory();

  setTimeout(() => setShowLoginErrorMessage(true), 2000);

  const [chatWindowState, setChatWindowState] = useState(false);
  const [closeChatButtonState, setCloseChatButtonState] = useState(false);

  const toggleChatWindow = () => {
    if (!chatWindowState) {
      setChatWindowState(true);
      setCloseChatButtonState(true);
      if (!isMobile) requestAnimationFrame(() => updateDimensions());

      var ipAddress = data["ipAddress"];
      var city = data["city"];
      var country = data["country"];
      if (typeof ipAddress === "undefined") ipAddress = "localhost";

      sendChatMessage(
        "AdminTool chat clicked by " +
          ipAddress +
          (ipAddress !== "localhost" ? " in " + city + "," + country : "") +
          " (" +
          data["environmentName"] +
          ")",
      );
    } else {
      setChatWindowState(false);
      setCloseChatButtonState(false);
    }
  };

  useEffect(() => {
    if (chatWindowState && closeChatButtonState && !isMobile) {
      updateDimensions();
    }
  }, [chatWindowState, closeChatButtonState]);

  useEffect(() => {
    const cookies = document.cookie.split("; ");
    for (const cookie of cookies) {
      const [key, value] = cookie.split("=");
      if (key === "finish_register_userID") {
        const userId = decodeURIComponent(value);
        if (!userId?.includes(":")) {
          setFinishRegistration(true);
          document.cookie =
            "finish_register_userID=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        }
      }
    }
    setTimeout(() => setShowChatBotPopup(true), 5000);
    setTimeout(() => setShowChatBotPopup(false), 15000);
    setTimeout(() => setShowChatBotPopup(true), 60000);
    setTimeout(() => setShowChatBotPopup(false), 70000);

    return () => {
      setFinishRegistration(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUserDeep(userId) {
    try {
      if (globalData.debug) console.log("#######User = " + userId);
      const response = await fetchSecure("/api/getUser/" + userId);
      const data = await response.json();
      setUserDeep(data && typeof data === "object" ? data : {});
      setUserData(data && typeof data === "object" ? data : {});
    } catch (e) {
      console.log("Failed to load user!");
      setUser({});
    }
  }

  async function registerLoggedInUser(userId) {
    try {
      setUser(userId);
      sendChatMessage("IN HERE registerLoggedInUser - " + userId);

      const response1 = await fetchSecure("/api/getAddUser/" + userId);
      const data = await response1.json();
      setUserDeep(data["userObject"] || {});
      setUserData(data["userObject"] || {});
      sessionStorage.setItem("userDeepObj", JSON.stringify(data));
      const response2 = await fetchSecure("/api/createSession/" + userId);
      const session = await response2.json();

      const expirationDate = new Date();
      expirationDate.setTime(expirationDate.getTime() + 10 * 60 * 1000);

      sessionStorage.setItem("sessionId", session["sessionId"]);
      setAuthConfig({ sessionId: session["sessionId"] });

      if (
        !["trial", "unregistered", "freemium"]?.includes(
          data?.userObject?.planName,
        )
      ) {
        if (false && typeof history !== "undefined") {
          history.push("/agents");
        }
      }
    } catch (e) {
      console.log("Failed to create session!");
      console.error("App: Failed to registerLoggedInUser", e);
      console.log(e.stack);
    }
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  async function initializeUser(userId) {
    try {
      console.log("IN HERE initializeUser - " + userId);
      sendChatMessage("IN HERE initializeUser - " + userId);

      setUser(userId);
      await delay(2000);
      const response = await fetchSecure("/api/getAddUser/" + userId);
      const data = await response.json();

      setUserDeep(data["userObject"] || {});
      setUserData(data["userObject"] || {});
    } catch (e) {
      console.log("Failed to initializeUser!");
      console.error("App: Failed to initializeUser", e);
      console.log(e.stack);
    }
  }

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      try {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());

        if (typeof localStorage.getItem("token") !== "undefined") {
          const sessionId = authConfig.sessionId;
          if (
            sessionId &&
            sessionId.length > 0 &&
            !localStorage.getItem("isLoggedIn")
          ) {
            const response = await fetchSecure(
              "/api/getSession/" + encodeURIComponent(sessionId),
            );
            const session = await response.json();
            if (session["userId"]) {
              localStorage.setItem("isLoggedIn", "True");
              setTimeout(() => {
                localStorage.removeItem("isLoggedIn");
              }, 2000);
              await initializeUser(session["userId"]);
            } else {
              console.warn("App: No userId in session response", session);
            }
          }
        }

        if ("id" in params) setId(params["id"]);
        else setId("");

        if (
          "instance" in params &&
          window.location.pathname.indexOf("plan_table_with_buttons") < 0
        ) {
          const response = await fetchSecure(
            "/api/getWixInfo/" + params["instance"],
          );
          const data = await response.json();

          if (!data["user"]) {
            history.push("/signin");
            return;
          }

          localStorage.setItem("wixToken", data["wixToken"]);
          await registerLoggedInUser(data["user"]);
          setUser(data["user"]);
          setId(data["id"]);
          setIframeLogin(true);
        } else {
          setIframeLogin(false);
        }

        if (
          "instance" in params &&
          window.location.pathname.indexOf("plan_table_with_buttons") >= 0
        ) {
          /* const plans = await WixGetPlans();
          var planIdArray = [];
          for (let x = 0; x < plans["_items"].length; x++) {
            planIdArray.push({
              _id: plans["_items"][x]["_id"],
              name: plans["_items"][x]["name"].toLowerCase(),
            });
          }
          setPlanIds(planIdArray); */
        }
      } catch (e) {
        console.error("App: loadPage failed", e);
      } finally {
        setLoading(false);
      }
    }
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location, window.location.search]);

  useEffect(() => {
    if (globalData.debug) console.log("IN HERE App useEffect");
    async function loadPage() {
      if (false && user && user.length > 0) await loadUserDeep(user);
      if (user && typeof user === "string" && user.length > 0) {
        initDataCache(user);
      }
    }
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if ("id" in params) setId(params["id"]);
    else setId("");
  }, [history]);

  function showAgentDetails() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if ("id" in params) return true;
    if (iframeLogin) return true;
    if (id && id.length > 0) return true;
    return false;
  }

  function isSystemAdmin() {
    if (
      userDeep &&
      userDeep.roles &&
      ((typeof userDeep.roles === "string" &&
        userDeep.roles.toLowerCase().indexOf("system_admin") >= 0) ||
        (Array.isArray(userDeep.roles) &&
          userDeep.roles.includes("system_admin")))
    )
      return true;
    return false;
  }

  function isDev() {
    const href = window.location.href || "";
    return (
      href.includes("local") ||
      href.includes("dev") ||
      href.includes("localhost") ||
      href.includes("superapp-479920")
    );
  }

  function isTester() {
    if (
      userDeep &&
      userDeep.roles &&
      userDeep.roles.toLowerCase().indexOf("tester") >= 0
    )
      return true;
    return false;
  }

  function defaultMenu() {
    try {
      if (userDeep && ["silver", "gold"]?.includes(userDeep?.planName)) {
        return "/agents";
      }
      if (
        pathName.length > 0 &&
        (pathName.indexOf("/") > 0 ||
          pathName === "whitepapers" ||
          pathName === "quickstart")
      ) {
        if (pathName.indexOf("/") > 0)
          pathName = pathName.substring(0, pathName.indexOf("/"));
        return "/" + pathName;
      } else return "/home";
    } catch (e) {
      console.log(`defaultMenu failed`);
      console.log(e);
      console.log(e.stack);
      return "/home";
    }
  }

  const baseStyle = { transition: "transform 0.3s ease" };
  const hoverStyle = { transform: "scale(1.2)" };
  const imageStyle = {
    borderRadius: "50%",
    transition: "transform 0.3s ease",
    border: "2px solid white",
  };

  function rotateCircle(elementId, duration) {
    const element = document.getElementById(elementId);
    let start = null;
    function animate(timestamp) {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const rotation = (progress / duration) * 360;
      element.style.transform = `rotate(${rotation}deg)`;
      if (progress < duration) {
        requestAnimationFrame(animate);
      } else {
        element.style.transform = `rotate(0deg)`;
        requestAnimationFrame(animate);
      }
    }
    requestAnimationFrame(animate);
  }

  function rotateCircleCss(elementId, duration) {
    const element = document.getElementById(elementId);
    element.style.transition = `transform ${duration / 1000}s linear`;
    element.style.transform = `rotate(360deg)`;
    setTimeout(() => {
      element.style.transition = "none";
      element.style.transform = "rotate(0deg)";
      setTimeout(() => {
        rotateCircleCss(elementId, duration);
      }, 10);
    }, duration);
  }

  // Theme injection is now handled by ThemeProvider

  if (configLoading) return <Loading />;
  if (configError)
    return <div className="error-screen">Error loading app: {configError}</div>;
  if (false && isMobile) {
    return <div>This app performs better on a computer browser</div>;
  } else
    return (
      <ThemeProvider appId={appId}>
        <GlobalContext.Provider value={{ ...globalVariables, config: cfg }}>
          <>
            {data["showAdminToolChat"] === true &&
              pathName.indexOf("plan_table_no_header") < 0 && (
                <>
                  {false && showChatBotPopup && (
                    <div
                      style={{
                        display: chatWindowState ? "none" : "block",
                        position: "fixed",
                        bottom: "100px",
                        borderRadius: "10px",
                        right: "100px",
                        background: "lightblue",
                        color: "black",
                        width: "150px",
                        border: "2px solid black",
                        padding: "10px",
                        zIndex: 999,
                      }}
                    >
                      Check out our awesome chatbot.See what kind of cool things
                      it can do
                    </div>
                  )}
                  <button
                    id="AgentIcon"
                    style={{
                      position: "fixed",
                      backgroundColor: "transparent",
                      border: "none",
                      bottom: isMobile ? "15px" : "20px",
                      right: isMobile ? "15px" : "20px",
                      zIndex: "998",
                      transition: "transform 0.3s ease",
                    }}
                    onClick={toggleChatWindow}
                    onMouseEnter={(e) => {
                      if (true) {
                        if (e.currentTarget) {
                          Object.assign(e.currentTarget.style, hoverStyle);
                        }
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (true && e.currentTarget) {
                        e.currentTarget.style.transform = "scale(1)";
                      }
                    }}
                  >
                    <img
                      alt="Chatbot"
                      src="https://storage.googleapis.com/koodums-chatbot-images/Koodums_Icon_no_bg.png"
                      height={isMobile ? "75px" : "90px"}
                      width={isMobile ? "75px" : "90px"}
                      style={imageStyle}
                    />
                  </button>
                  {chatbotSrc && (
                    <div
                      id="ChatBotHost"
                      style={{
                        position: "fixed",
                        bottom: isMobile ? "15px" : "20px",
                        right: isMobile ? "15px" : "20px",
                        zIndex: 999,
                        opacity: chatWindowState ? 1 : 0,
                        visibility: chatWindowState ? "visible" : "hidden",
                        pointerEvents: chatWindowState ? "auto" : "none",
                        transition:
                          "opacity 120ms ease-out, visibility 0s linear",
                      }}
                    >
                      {/* <ChatBot iframeSrc={chatbotSrc} /> */}
                    </div>
                  )}
                  {chatWindowState && closeChatButtonState && (
                    <button
                      className="chatCloseBtn"
                      id="chatCloseBtn"
                      onClick={toggleChatWindow}
                    >
                      <svg
                        className="arrow-down"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="black"
                        stroke="#292929"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </button>
                  )}
                </>
              )}
            <Router>
              <div className="main-body-content">
                {/**
                 * HEADER COMPONENT RENDERING
                 *
                 * Renders the main application header with navigation menu.
                 * Header configuration comes from SuperApp config (loaded above):
                 * - Title: cfg.header.title (also sets document.title)
                 * - Logo: cfg.header.brand.logoUrl
                 * - Menu Items: cfgMenuItems (extracted from header.primaryMenu.items)
                 * - Features: cfg.features (showLogin, showTryForFree buttons)
                 *
                 * The Header component:
                 * - Renders navigation menu from cfgMenuItems
                 * - Displays brand logo and title
                 * - Shows login/trial buttons based on feature flags
                 * - Handles user authentication state
                 * - Listens to 'superapp-config-updated' event for real-time updates
                 *
                 * Conditional rendering:
                 * - Only renders if iframeLogin is false (not in Wix iframe)
                 * - Hidden on plan_table_no_header pages
                 *
                 * @see Header.js component for implementation details
                 */}
                {iframeLogin !== null &&
                  iframeLogin === false &&
                  pathName.indexOf("plan_table_no_header") < 0 && (
                    <Header
                      users={users}
                      userDeep={userDeep}
                      user={user}
                      reference={reference}
                      callback={registerLoggedInUser}
                      iframeLogin={iframeLogin}
                      isFinishRegistration={isFinishRegistration}
                      setFinishRegistration={setFinishRegistration}
                      defaultMenu={defaultMenu()}
                    />
                  )}
                {pathName.indexOf("plan_table") < 0 &&
                  iframeLogin !== null &&
                  iframeLogin === true && (
                    <></>
                    /* <HeaderWix
                    users={users}
                    userDeep={userDeep}
                    user={user}
                    reference={reference}
                    callback={registerLoggedInUser}
                    iframeLogin={iframeLogin}
                    isFinishRegistration={isFinishRegistration}
                    setFinishRegistration={setFinishRegistration}
                  /> */
                  )}
                <main>
                  <div className="main-area">
                    {/* Show loading screen until config is loaded from Firestore */}
                    {configLoading && (
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "60vh",
                          color: "#fff",
                          textAlign: "center",
                        }}
                      >
                        <div style={{ marginBottom: 16 }}>
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              border: "4px solid rgba(255,255,255,0.2)",
                              borderTop: "4px solid #fff",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                              margin: "0 auto",
                            }}
                          />
                          <style>{`
                        @keyframes spin {
                          0% { transform: rotate(0deg); }
                          100% { transform: rotate(360deg); }
                        }
                      `}</style>
                        </div>
                        <h2 style={{ color: "#fff", marginBottom: 8 }}>
                          Loading Configuration...
                        </h2>
                        <p style={{ color: "#fff", opacity: 0.7 }}>
                          Reading from Firestore...
                        </p>
                      </div>
                    )}
                    {configError && (
                      <div
                        style={{
                          padding: 24,
                          margin: 24,
                          background: "rgba(255,107,107,0.1)",
                          border: "1px solid rgba(255,107,107,0.3)",
                          borderRadius: 8,
                          color: "#ff6b6b",
                        }}
                      >
                        <h3 style={{ color: "#ff6b6b", marginTop: 0 }}>
                          Failed to Load Configuration
                        </h3>
                        <p>{configError}</p>
                        <p
                          style={{
                            fontSize: "0.9em",
                            opacity: 0.8,
                            marginTop: 16,
                          }}
                        >
                          The app will continue with cached configuration if
                          available.
                        </p>
                      </div>
                    )}
                    {!configLoading && (
                      <Switch>
                        {/* TEMP: Hardcoded route for /test to verify it works (dynamic routes from .map() not matching) */}
                        <Route
                          exact
                          path="/test"
                          render={(props) => {
                            console.log("🟢🟢🟢🟢🟢 TEST ROUTE MATCHED /test", {
                              pathname: props.location.pathname,
                              timestamp: Date.now(),
                            });
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                        {/* TEST: Simple route for /info to verify React Router is working */}
                        <Route
                          exact
                          path="/info"
                          render={(props) => {
                            console.log("🟢🟢🟢🟢🟢 TEST ROUTE MATCHED /info", {
                              pathname: props.location.pathname,
                              timestamp: Date.now(),
                              props: props,
                            });
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                        {/* TEST: Simple routes to verify React Router is working */}
                        <Route
                          exact
                          path="/docs/guides"
                          render={(props) => {
                            console.log(
                              "🟢🟢🟢 TEST ROUTE MATCHED /docs/guides",
                              {
                                pathname: props.location.pathname,
                                timestamp: Date.now(),
                                props: props,
                              },
                            );
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                        <Route
                          exact
                          path="/docs/api"
                          render={(props) => {
                            console.log("🟢🟢🟢 TEST ROUTE MATCHED /docs/api", {
                              pathname: props.location.pathname,
                              timestamp: Date.now(),
                              props: props,
                            });
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                        <Route
                          exact
                          path="/products"
                          render={(props) => {
                            console.log("🟢🟢🟢 TEST ROUTE MATCHED /products", {
                              pathname: props.location.pathname,
                              timestamp: Date.now(),
                              props: props,
                            });
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                        <Route
                          exact
                          path="/about"
                          render={(props) => {
                            console.log("🟢🟢🟢 TEST ROUTE MATCHED /about", {
                              pathname: props.location.pathname,
                              timestamp: Date.now(),
                              props: props,
                            });
                            return <LowCodePage appId={appId} />;
                          }}
                        />

                        {/* Debug: Log current pathname when Switch renders */}
                        {(() => {
                          const currentPath = window.location.pathname;
                          if (
                            currentPath.includes("/docs/") ||
                            currentPath === "/products"
                          ) {
                            console.log("🔴 Switch rendering", {
                              currentPath,
                              timestamp: Date.now(),
                              layoutRoutes: Array.from(layoutRouteSet),
                              isProductsInLayout:
                                layoutRouteSet.has("/products"),
                              hasCfg: !!cfg,
                              hasMenuItems: !!cfgMenuItems,
                              menuItemCount: cfgMenuItems?.length || 0,
                            });
                          }
                          return null;
                        })()}

                        {/* Debug: Log current pathname when Switch renders */}
                        {(() => {
                          const currentPath = window.location.pathname;
                          if (currentPath === "/products") {
                            console.log(
                              "🔴 Switch rendering - currently on /products",
                              {
                                currentPath,
                                timestamp: Date.now(),
                              },
                            );
                          }
                          return null;
                        })()}
                        {/* Debug: Log route matching */}
                        {(() => {
                          console.log(
                            "🔵 App.js Route Switch - Config State:",
                            {
                              configLoading,
                              hasConfig: !!cfg,
                              hasScreens: !!cfg?.screens,
                              screenKeys: cfg?.screens
                                ? Object.keys(cfg.screens)
                                : [],
                              appId,
                              currentPath: window.location.pathname,
                              layoutRoutes: layoutRoutes,
                              layoutRouteSet: Array.from(layoutRouteSet),
                              isProductsInLayout:
                                layoutRouteSet.has("/products"),
                            },
                          );
                          return null;
                        })()}

                        {/* Home route - check if screen config exists, use LowCodePage if it does */}
                        {(() => {
                          const homeScreen = cfg?.screens?.home;
                          // If home screen is a layout type, use LowCodePage to render it properly
                          if (homeScreen && homeScreen.type === "layout") {
                            return (
                              <Route exact path="/home">
                                <LowCodePage appId={appId} />
                              </Route>
                            );
                          }
                          // Otherwise, use the default Home component
                          if (!layoutRouteSet.has("/home")) {
                            return (
                              <Route exact path="/home">
                                <Home
                                  user={user}
                                  userDeep={userDeep}
                                  showLoginErrorMessage={showLoginErrorMessage}
                                  isFinishRegistration={isFinishRegistration}
                                />
                              </Route>
                            );
                          }
                          return null;
                        })()}

                        {/* Auth routes */}
                        <PublicRoute
                          exact
                          path="/signin"
                          component={Signin}
                          isLoggedIn={isLoggedIn()}
                          restricted={false}
                          callback={registerLoggedInUser}
                        />
                        <PublicRoute
                          exact
                          path="/signup"
                          component={Signup}
                          isLoggedIn={isLoggedIn()}
                          restricted={false}
                          callback={registerLoggedInUser}
                        />

                        {/* Simple front-end editor - only accessible when logged in */}
                        {isLoggedIn() && (
                          <Route exact path="/newmenu">
                            <NewMenu />
                          </Route>
                        )}

                        {/* Page Editor - MUST be before dynamic routes to match first */}
                        {/* Allow access if dev environment OR if logged in and system admin */}
                        {(() => {
                          const isLoggedInValue = isLoggedIn();
                          const isSystemAdminValue = isSystemAdmin();
                          const isDevValue = isDev();
                          // In dev environment, allow access even if not fully logged in yet
                          // Simplified: if dev, always allow. Otherwise require login + (system admin OR dev)
                          const canAccess = isDevValue
                            ? true
                            : isLoggedInValue &&
                              (isSystemAdminValue || isDevValue);
                          console.log("🔵 Page Editor Route Check:", {
                            isLoggedIn: isLoggedInValue,
                            isSystemAdmin: isSystemAdminValue,
                            isDev: isDevValue,
                            canAccess,
                            canAccessCalculation: `${isDevValue} ? true : (${isLoggedInValue} && (${isSystemAdminValue} || ${isDevValue}))`,
                            currentPath: window.location.pathname,
                            user: user,
                            hasUserDeep: !!userDeep,
                          });
                          if (canAccess) {
                            return (
                              <Route path="/page-editor/:appId?">
                                {() => {
                                  return <PageEditor />;
                                }}
                              </Route>
                            );
                          }
                          return null;
                        })()}

                        {/* 
                    Menu Editor Route
                    
                    IMPORTANT: This route is always rendered (not conditionally wrapped in a check).
                    This ensures React Router can always match the route, preventing blank pages
                    when navigating to /menu-editor.
                    
                    The access check happens inside the route render, showing an "Access Denied"
                    message if the user is not logged in, rather than rendering nothing (blank page).
                    
                    The MenuEditor component itself can perform additional permission checks
                    (e.g., system admin, dev mode) and display appropriate messages.
                  */}
                        <Route exact path="/menu-editor">
                          {isLoggedIn() ? (
                            <MenuEditor />
                          ) : (
                            <div
                              style={{
                                padding: "24px",
                                color: "#fff",
                                textAlign: "center",
                              }}
                            >
                              <h2>Access Denied</h2>
                              <p>
                                You must be logged in to access the Menu Editor.
                              </p>
                            </div>
                          )}
                        </Route>
                        {isLoggedIn() && (isSystemAdmin() || isDev()) && (
                          <Route exact path="/edit-mode">
                            <EditMode />
                          </Route>
                        )}

                        {/* Header Builder Route */}
                        <Route exact path="/header-manager">
                          {isLoggedIn() ? (
                            <HeaderManager />
                          ) : (
                            <div
                              style={{
                                padding: "24px",
                                color: "#fff",
                                textAlign: "center",
                              }}
                            >
                              <h2>Access Denied</h2>
                              <p>
                                You must be logged in to access the Header
                                Manager.
                              </p>
                            </div>
                          )}
                        </Route>

                        {/* Unified Editor Route */}
                        <Route exact path="/unified-editor">
                          {isLoggedIn() ? (
                            /* Vik (Jan 21, 2026) : Pass userDeep to UnifiedEditor to enable role-based access control (e.g., system_admin checks).*/
                            <UnifiedEditor userDeep={userDeep} />
                          ) : (
                            <div
                              style={{
                                padding: "24px",
                                color: "#fff",
                                textAlign: "center",
                              }}
                            >
                              <h2>Access Denied</h2>
                              <p>
                                You must be logged in to access the Unified
                                Editor.
                              </p>
                            </div>
                          )}
                        </Route>

                        {/**
                         * DYNAMIC ROUTE CREATION FROM MENU ITEMS
                         *
                         * Creates React Router routes from SuperApp config menu items.
                         * These routes render LowCodePage component which loads page config
                         * from screens[pageKey] and renders the layout.
                         *
                         * Route Priority (important for matching):
                         * 1. Child routes created FIRST (more specific, e.g., /docs/guides)
                         * 2. Parent routes created SECOND (less specific, e.g., /docs)
                         * 3. Layout routes (from layout.route)
                         * 4. Screen routes (from screens object)
                         * 5. Catch-all route (*)
                         *
                         * Route matching:
                         * - Uses exact path matching to prevent conflicts
                         * - Child routes must be created before parent routes
                         * - LowCodePage component extracts pageKey from URL path
                         *
                         * Example:
                         * Menu item: { key: "docs", path: "/docs", children: [{ key: "guides", path: "/docs/guides" }] }
                         * Creates routes:
                         *   - /docs/guides → LowCodePage (child route, matches first)
                         *   - /docs → LowCodePage (parent route)
                         *
                         * @see LowCodePage component for page rendering logic
                         */}
                        {/* Dynamic routes from SuperApp config -> LowCode renderer */}
                        {/* IMPORTANT: Create these BEFORE layout routes so they match first */}
                        {cfgMenuItems && cfgMenuItems.length > 0 && (
                          <>
                            {/* IMPORTANT: Create child routes FIRST so they match before parent routes */}
                            {cfgMenuItems
                              .filter(
                                (m) =>
                                  m &&
                                  m.visible !== false &&
                                  Array.isArray(m.children),
                              )
                              .flatMap((m) => {
                                const basePath =
                                  m.path ||
                                  m.to ||
                                  `/${String(m.key || "").toLowerCase()}`;
                                console.log(
                                  `🔵 Processing menu item with children: ${basePath}`,
                                  {
                                    menuItem: m,
                                    childrenCount: m.children?.length || 0,
                                  },
                                );
                                return m.children
                                  .filter((c) => c && c.visible !== false)
                                  .map((c, i) => {
                                    const cKey = String(
                                      c.key || c.label || `c${i}`,
                                    ).toLowerCase();
                                    const cPathRaw =
                                      c.path || c.to || `/${cKey}`;
                                    const cPath = cPathRaw.startsWith("/")
                                      ? cPathRaw
                                      : `/${cPathRaw}`;
                                    console.log(
                                      `🔵 Creating child route: ${cPath}`,
                                      {
                                        child: c,
                                        parent: basePath,
                                        cPathRaw,
                                        cPath,
                                      },
                                    );
                                    if (
                                      layoutRouteSet.has(cPath) ||
                                      cPath === "/home"
                                    ) {
                                      return null;
                                    }
                                    // Double-check the path is correct
                                    const finalPath = cPath;
                                    console.log(
                                      `🔵🔵🔵 About to create Route component for: ${finalPath}`,
                                      {
                                        child: c,
                                        cPathRaw,
                                        finalPath,
                                        willMatch:
                                          window.location.pathname ===
                                          finalPath,
                                      },
                                    );
                                    return (
                                      <Route
                                        key={`dyn-child-${basePath}-${finalPath}`}
                                        exact
                                        path={finalPath}
                                        render={(props) => {
                                          console.log(
                                            `🟢🟢🟢🟢🟢 Dynamic child route MATCHED: ${finalPath}`,
                                            {
                                              appId,
                                              pathname: props.location.pathname,
                                              routePath: finalPath,
                                              match: props.match,
                                              hasConfig: !!cfg,
                                              hasScreens: !!cfg?.screens,
                                              routeKey: finalPath.replace(
                                                /^\//,
                                                "",
                                              ),
                                              timestamp: Date.now(),
                                            },
                                          );
                                          return <LowCodePage appId={appId} />;
                                        }}
                                      />
                                    );
                                  });
                              })}
                            {/* Then create parent routes (these should have exact to avoid matching children) */}
                            {cfgMenuItems
                              .filter(
                                (m) =>
                                  m &&
                                  m.visible !== false &&
                                  (m.path || m.to || m.key),
                              )
                              .map((m) => {
                                const basePath =
                                  m.path ||
                                  m.to ||
                                  `/${String(m.key || "").toLowerCase()}`;
                                const normalizedBase = basePath.startsWith("/")
                                  ? basePath
                                  : `/${basePath}`;
                                // Skip /home - it should use the Home component, not LowCodePage
                                // Skip if it's a layout route (handled separately)
                                if (
                                  layoutRouteSet.has(normalizedBase) ||
                                  normalizedBase === "/home"
                                ) {
                                  return null;
                                }
                                console.log(
                                  `🔵 Creating dynamic route for menu item: ${normalizedBase}`,
                                  {
                                    menuItem: m,
                                    basePath,
                                    normalizedBase,
                                    isInLayout:
                                      layoutRouteSet.has(normalizedBase),
                                  },
                                );
                                return (
                                  <Route
                                    key={`dyn-${normalizedBase}`}
                                    exact
                                    path={normalizedBase}
                                    render={(props) => {
                                      console.log(
                                        `🟢🟢🟢 Dynamic route matched: ${normalizedBase}`,
                                        {
                                          appId,
                                          pathname: props.location.pathname,
                                          hasConfig: !!cfg,
                                          hasScreens: !!cfg?.screens,
                                          routeKey: normalizedBase,
                                          timestamp: Date.now(),
                                          willRender: "LowCodePage",
                                        },
                                      );
                                      return <LowCodePage appId={appId} />;
                                    }}
                                  />
                                );
                              })}
                          </>
                        )}

                        {/* Layout routes - created AFTER dynamic routes so they don't intercept */}
                        {layoutCfg &&
                          layoutRoutes.map((routePath) => {
                            // Skip /home if it's in layout routes - we handle it above
                            if (routePath === "/home") {
                              return null;
                            }
                            // Skip if this route is already handled by dynamic menu routes
                            const isInMenu = cfgMenuItems?.some((m) => {
                              const mPath = (
                                m.path ||
                                m.to ||
                                `/${String(m.key || "").toLowerCase()}`
                              ).startsWith("/")
                                ? m.path ||
                                  m.to ||
                                  `/${String(m.key || "").toLowerCase()}`
                                : `/${
                                    m.path ||
                                    m.to ||
                                    String(m.key || "").toLowerCase()
                                  }`;
                              if (mPath === routePath) return true;
                              if (Array.isArray(m.children)) {
                                return m.children.some((c) => {
                                  const cPath = (
                                    c.path ||
                                    c.to ||
                                    `/${String(
                                      c.key || c.label || "",
                                    ).toLowerCase()}`
                                  ).startsWith("/")
                                    ? c.path ||
                                      c.to ||
                                      `/${String(
                                        c.key || c.label || "",
                                      ).toLowerCase()}`
                                    : `/${
                                        c.path ||
                                        c.to ||
                                        String(
                                          c.key || c.label || "",
                                        ).toLowerCase()
                                      }`;
                                  return cPath === routePath;
                                });
                              }
                              return false;
                            });
                            if (isInMenu) {
                              return null;
                            }
                            return (
                              <Route
                                key={`layout-${routePath}`}
                                exact
                                path={routePath}
                                render={(props) => {
                                  console.log(
                                    `🟡 Layout route matched: ${routePath}`,
                                    {
                                      pathname: props.location.pathname,
                                      currentPath: window.location.pathname,
                                    },
                                  );
                                  return (
                                    <LayoutRenderer
                                      layout={layoutCfg}
                                      user={user}
                                      userDeep={userDeep}
                                    />
                                  );
                                }}
                              />
                            );
                          })}

                        {/* Fallback: Create routes for all screens that exist in config but aren't in menu */}
                        {cfg?.screens &&
                          Object.keys(cfg.screens).map((screenKey) => {
                            // Convert screen key to path (e.g., "docs/guides" -> "/docs/guides")
                            const screenPath = screenKey.startsWith("/")
                              ? screenKey
                              : `/${screenKey}`;
                            // Skip if already handled by menu routes or layout routes or home
                            if (
                              layoutRouteSet.has(screenPath) ||
                              screenPath === "/home"
                            ) {
                              return null;
                            }
                            // Check if route already exists from menu items
                            const menuHasRoute = cfgMenuItems.some((m) => {
                              const menuPath =
                                m.path ||
                                m.to ||
                                `/${String(m.key || "").toLowerCase()}`;
                              const normalizedMenuPath = menuPath.startsWith(
                                "/",
                              )
                                ? menuPath
                                : `/${menuPath}`;
                              if (normalizedMenuPath === screenPath) {
                                return true;
                              }
                              // Check children
                              if (Array.isArray(m.children)) {
                                return m.children.some((c) => {
                                  const cPathRaw =
                                    c.path ||
                                    c.to ||
                                    `/${String(
                                      c.key || c.label || "",
                                    ).toLowerCase()}`;
                                  const cPath = cPathRaw.startsWith("/")
                                    ? cPathRaw
                                    : `/${cPathRaw}`;
                                  if (cPath === screenPath) {
                                    return true;
                                  }
                                  return false;
                                });
                              }
                              return false;
                            });
                            if (menuHasRoute) {
                              return null;
                            }
                            return (
                              <Route
                                key={`screen-${screenKey}`}
                                exact
                                path={screenPath}
                                render={(props) => {
                                  console.log(
                                    `🟡 Fallback screen route matched: ${screenPath}`,
                                    {
                                      pathname: props.location.pathname,
                                    },
                                  );
                                  return <LowCodePage appId={appId} />;
                                }}
                              />
                            );
                          })}

                        {/* {isSystemAdmin() && (
                    <Route exact path="/system_admin">
                      <SystemAdmin
                        user={user}
                        userDeep={userDeep}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                  )}
                  {isTester() && (
                    <Route exact path="/tester">
                      <Tester
                        user={user}
                        userDeep={userDeep}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                  )} */}
                        {/* <Route exact path="/plan_table_no_header">
                    <NewPlanTable currentPlanName="none" showButtons={false} />
                  </Route>
                  <Route exact path="/plan_table">
                    <NewPlanTable currentPlanName="none" showButtons={false} />
                  </Route>
                  <Route exact path="/plan_table_with_buttons">
                    <NewPlanTable
                      currentPlanName={userDeep && userDeep.planName}
                      showWixButtons={true}
                      planIds={planIds}
                    />
                  </Route> */}
                        <Route exact path="/loading">
                          <Loading />
                        </Route>
                        {/* <Route exact path="/wix">
                    <Agents user={user} id={id} userDeep={userDeep} />
                  </Route> */}
                        <Route exact path="/begin_trial">
                          {!iframeLogin && defaultMenu() === "/home" && (
                            <Home
                              user={user}
                              userDeep={userDeep}
                              showLoginErrorMessage={showLoginErrorMessage}
                              isFinishRegistration={isFinishRegistration}
                            />
                          )}
                        </Route>
                        {(!layoutCfg || !layoutRouteSet.has("/")) && (
                          <Route exact path="/">
                            {/* {showAgentDetails() && <Agents user={user} id={id} userDeep={userDeep} />} */}
                            {!showAgentDetails() &&
                              !iframeLogin &&
                              defaultMenu() === "/home" && (
                                <Home
                                  user={user}
                                  userDeep={userDeep}
                                  showLoginErrorMessage={showLoginErrorMessage}
                                  isFinishRegistration={isFinishRegistration}
                                />
                              )}
                            {/* {!showAgentDetails() && !iframeLogin && defaultMenu() === "/agents" && (
                          <Agents
                            user={user}
                            userDeep={userDeep}
                            showLoginErrorMessage={showLoginErrorMessage}
                          />
                        )} */}
                          </Route>
                        )}
                        {/* <Route exact path="/stripe">
                    <Stripe user={user} userDeep={userDeep} showLoginErrorMessage={showLoginErrorMessage} />
                  </Route>
                  <Route exact path="/subscribe">
                    <Subscription />
                  </Route> */}
                        <Route exact path="/privacy">
                          <Privacy />
                        </Route>
                        <Route exact path="/tos">
                          <TOS />
                        </Route>
                        {/* <PublicRoute
                    exact
                    path="/capabilities"
                    component={Capabilities}
                    isLoggedIn={isLoggedIn()}
                    restricted={true}
                    environment={data["environmentName"]}
                  />
                  <Route exact path="/whitepapers">
                    <Whitepapers />
                  </Route>
                  <Route exact path="/whitepapers/:year/:month/:day">
                    <Whitepapers />
                  </Route> */}
                        {/* <Route exact path="/playground">
                      <Playground
                        user={user}
                        userDeep={userDeep}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                    <Route exact path="/integrations">
                      <Integrations
                        user={user}
                        userDeep={userDeep}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                    <Route exact path="/marketing">
                      <Marketing
                        user={user}
                        userDeep={{ ...userDeep, isBlog: true }}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                    <Route exact path="/console">
                      <Console
                        user={user}
                        userDeep={{ ...userDeep, isBlog: true }}
                        showLoginErrorMessage={showLoginErrorMessage}
                      />
                    </Route>
                    <Route exact path="/agents">
                      {showAgentDetails() && <Agents user={user} id={id} userDeep={userDeep} />}
                      {!showAgentDetails() && (
                        <Agents
                          user={user}
                          userDeep={userDeep}
                          showLoginErrorMessage={showLoginErrorMessage}
                        />
                      )}
                    </Route>
                    <PrivateRoute
                      exact
                      path="/user_admin"
                      component={UserAdmin}
                      isLoggedIn={isLoggedIn()}
                      isLoading={isLoading}
                      user={user}
                      userDeep={userDeep}
                      showLoginErrorMessage={showLoginErrorMessage}
                    /> */}
                        {/**
                         * CATCH-ALL ROUTE
                         *
                         * Matches any route that hasn't been matched by previous routes.
                         * Renders LowCodePage which:
                         * - Attempts to load page config from screens[pageKey]
                         * - If page exists: Renders the configured layout
                         * - If page doesn't exist: Shows "Build This Page" button linking to PageEditor
                         *
                         * This allows users to navigate to any URL and either see the page
                         * (if configured) or get a prompt to create it.
                         *
                         * @see LowCodePage component for page loading and rendering logic
                         */}
                        {/* Catch-all route - use LowCodePage so it can show "Build This Page" button */}
                        <Route
                          path="*"
                          render={(props) => {
                            console.log("🔴🔴🔴🔴🔴 CATCH-ALL ROUTE MATCHED", {
                              pathname: props.location.pathname,
                              currentPath: window.location.pathname,
                              willRender: "LowCodePage",
                            });
                            // For any unmatched path, use LowCodePage
                            // It will show "Build This Page" button if screen doesn't exist
                            return <LowCodePage appId={appId} />;
                          }}
                        />
                      </Switch>
                    )}
                  </div>
                </main>
              </div>
            </Router>
          </>
        </GlobalContext.Provider>
      </ThemeProvider>
    );
}

function App() {
  const [user, loading] = useAuthState(auth);

  // Sanitize appId to prevent double query params
  const rawAppId = new URLSearchParams(window.location.search).get("appId");
  const appId = rawAppId ? rawAppId.split("?")[0] : null;

  const isRoot =
    window.location.pathname === "/" || window.location.pathname === "";

  if (loading) {
    return <Loading />;
  }

  // If user is logged in but no appId, show App Selector (unless we are on a specific route that doesn't need it)
  if (user && !appId) {
    return (
      <Router>
        <Switch>
          <Route path="/" component={AppSelector} />
        </Switch>
      </Router>
    );
  }

  return (
    <Router>
      <Switch>
        <Route
          path="/builder/:pageId"
          render={(props) => <VisualBuilder {...props} />}
        />
        {/* Vik (Jan 22, 2026) : page-editor route removed from here - now handled inside MainApp so Header navbar is visible */}
        <Route path="/">
          <MainApp appId={appId} user={user} />
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
